/**
 * Branded ID Types
 *
 * Type-safe ID definitions for all entities. Using branded types prevents
 * accidentally passing the wrong ID type at compile time.
 *
 * Example:
 *   function getBlock(id: BlockId): Block { ... }
 *   getBlock(artifactId); // Compile error! ArtifactId not assignable to BlockId
 *
 * These are "phantom types" - at runtime they're just strings, but TypeScript
 * treats them as distinct types.
 *
 * IMPORTANT: These types use Zod's $brand format for compatibility with
 * Zod schemas created via .brand<T>(). This ensures type consistency
 * between manually typed IDs and Zod-parsed IDs.
 */
import { z } from "zod";
import { customAlphabet } from "nanoid";
// ---------------------------------------------------------------------------
// ID Format Constants & Prefix Mapping
// ---------------------------------------------------------------------------
/** Length of the nanoid portion of prefixed IDs */
export const PREFIXED_ID_NANOID_LENGTH = 24;
/**
 * Regex pattern for validating any prefixed ID.
 * Format: {2-5 lowercase letters}_{24 lowercase alphanumeric chars}
 */
export const PREFIXED_ID_PATTERN = /^[a-z]{2,5}_[a-z0-9]{24}$/;
/**
 * Entity type to prefix mapping.
 * Keep prefixes short (3-5 chars) for readability.
 */
export const ENTITY_PREFIXES = {
    // Core entities
    block: "blk",
    tag: "tag",
    taxonomy: "tax",
    artifact: "art",
    session: "ses",
    activity: "act",
    capture: "cap",
    capturePermission: "cperm",
    systemEvent: "evt",
    annotation: "ann",
    userAnnotation: "uan",
    mention: "mnt",
    reminder: "rem",
    collection: "col",
    collectionMember: "cmem",
    blockLineage: "blin",
    blockLineageEvent: "blev",
    blockEditLock: "blok",
    pin: "pin",
    // Link/junction tables
    blockTag: "btag",
    blockTaxonomy: "btax",
    taxonomyLink: "txlnk",
    // Internal/transient
    candidate: "cand",
    // Observability / error logs
    failedSearchQuery: "fsq",
    failedBlockCreation: "fbc",
    notification: "ntf",
};
// ---------------------------------------------------------------------------
// Zod Schema Helpers
// ---------------------------------------------------------------------------
/**
 * Create a Zod schema for validating prefixed IDs.
 * Automatically looks up the prefix from the entity type.
 *
 * @param entity - The entity type (e.g., "block", "tag")
 * @returns A Zod string schema with regex validation
 *
 * @example
 * prefixedIdSchema("block")  // validates "blk_..." format
 * prefixedIdSchema("tag")     // validates "tag_..." format
 */
export function prefixedIdSchema(entity) {
    const prefix = ENTITY_PREFIXES[entity];
    const pattern = new RegExp(`^${prefix}_[a-z0-9]{${PREFIXED_ID_NANOID_LENGTH}}$`);
    return z.string().regex(pattern, {
        message: `Expected ${entity} ID with format "${prefix}_[${PREFIXED_ID_NANOID_LENGTH} chars]"`,
    });
}
// ---------------------------------------------------------------------------
// ID Generation
// ---------------------------------------------------------------------------
const ALPHABET = "0123456789abcdefghijklmnopqrstuvwxyz";
const nanoid = customAlphabet(ALPHABET, PREFIXED_ID_NANOID_LENGTH);
/**
 * Generate a new prefixed ID for an entity.
 * Returns a branded type for compile-time safety.
 * Works in both Node and browsers (uses crypto.getRandomValues() under the hood).
 *
 * @example
 * const blockId = generateId("block");  // "blk_v1stgxr8z5jdhi6bmyt3x"
 */
export function generateId(entity) {
    const prefix = ENTITY_PREFIXES[entity];
    return `${prefix}_${nanoid()}`;
}
// ---------------------------------------------------------------------------
// ID Utilities
// ---------------------------------------------------------------------------
const NANOID_PATTERN = /^[a-z0-9]+$/;
/**
 * Validates any prefixed ID without checking the specific entity type.
 */
function isValidAnyPrefixedId(value) {
    const underscoreIndex = value.indexOf("_");
    if (underscoreIndex === -1)
        return false;
    const prefix = value.substring(0, underscoreIndex);
    const suffix = value.substring(underscoreIndex + 1);
    const validPrefixes = Object.values(ENTITY_PREFIXES);
    if (!validPrefixes.includes(prefix))
        return false;
    if (suffix.length !== PREFIXED_ID_NANOID_LENGTH)
        return false;
    if (!NANOID_PATTERN.test(suffix))
        return false;
    return true;
}
export const anyPrefixedId = z.string().refine(isValidAnyPrefixedId, {
    message: "Invalid prefixed ID format",
});
/**
 * Extract the entity type from a prefixed ID.
 *
 * @example
 * getEntityType("blk_v1stgxr8z5jdhi6bmyt3x") // "block"
 * getEntityType("tag_k2a98xxat96iws9zmbrgj") // "tag"
 * getEntityType("unknown_abc")                // undefined
 */
export function getEntityType(id) {
    const underscoreIndex = id.indexOf("_");
    if (underscoreIndex === -1)
        return undefined;
    const prefix = id.substring(0, underscoreIndex);
    const entries = Object.entries(ENTITY_PREFIXES);
    const match = entries.find(([, p]) => p === prefix);
    return match?.[0];
}
/**
 * Check if an ID has the expected entity type prefix.
 *
 * @example
 * isEntityType("blk_abc123", "block") // true
 * isEntityType("tag_abc123", "block") // false
 */
export function isEntityType(id, entity) {
    const prefix = ENTITY_PREFIXES[entity];
    return id.startsWith(`${prefix}_`);
}
